<template>
  <div
    tabindex="0"
    class="size-guide-table"
    :class="{
      [$class('active')]: anyActive
    }"
    @mouseleave="hoverColumn = null"
  >
    <table class="size-guide-table__table">
      <tr class="size-guide-table__header">
        <th
          v-for="(header, headerIndex) of table?.fields"
          :key="headerIndex"
          class="size-guide-table__header-cell text-desktop-mini-uc"
          :class="{
            [$class('active')]: headerIndex === hoverColumn
              || headerIndex === activeColumn
          }"
          @mouseenter="hoverColumn = headerIndex"
          @click="() => handleCellClick(headerIndex)"
          v-html="formatHeaderField(header.toLowerCase())"
        />
      </tr>

      <tr
        v-for="(row, rowIndex) of formattedData"
        :key="rowIndex"
        class="size-guide-table__body"
      >
        <td
          v-for="(cell, cellIndex) of formatedRowData(Object.values(row))"
          :key="cellIndex"
          class="size-guide-table__body-cell text-desktop-mini-uc"
          :class="{
            'size-guide-table__body-cell--first': cellIndex === 0,
            [$class('active')]: cellIndex === hoverColumn
              || cellIndex === activeColumn
          }"
          @click="() => handleCellClick(cellIndex)"
          @mouseenter="hoverColumn = cellIndex"
          v-text="formatTableCells(cell, cellIndex)"
        />
      </tr>
    </table>
  </div>
</template>

<script>

/**
 * Vue: Size guide table (size-guide-table)
 * -----------------------------------------------------------------------------
 * Size guide table for rendering data parsed from CSV.
 *
 * @param {Object} table - Table data parsed from CSV.
 * @param {Boolean} showInches - Whether to display the data in cm or inches.
 * @param {null, Number} variantColumn - Column the corresponding variant
 * product relates to.
 */
export default {
  name: 'SizeGuideTable',

  props: {
    emitsClickEvents: {
      type: Boolean,
      default: false,
    },

    table: {
      type: Object,
      default: () => ({
        data: [],
        fields: [],
      }),
    },

    showInches: {
      type: Boolean,
      default: false,
    },

    variantColumn: {
      type: [null, Number],
      default: null,
    },
  },

  emits: {
    'column-updated': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      hoverColumn: null,
      activeColumn: null,
      position: {},
      tableElement: null,
    }
  },

  computed: {

    /**
     * Indicates if either a column is selected or hovered.
     * @returns {Boolean}
     */
    anyActive() {
      return Boolean(this.hoverColumn || this.activeColumn)
    },

    /**
     * Removes empty lines from the size guide table.
     * @returns {Array}
     */
    formattedData() {
      return this?.table?.data
    },

    /**
     * Checks if certain conditions exist that enable a table to be
     * built/displayed.
     * @returns {Boolean}
     */
    showTable() {
      if (!this.formattedData?.length || !this.table?.fields?.length) {
        return false
      }

      const data = this.formattedData.every(row => Array.isArray(Object.values(row)))
      return Boolean(data)
    },
  },

  watch: {

    /**
     * When the variant column updates, we update the active size in the guide.
     */
    variantColumn: {
      immediate: true,
      handler(column) {
        if (typeof column !== 'number') {
          this.activeColumn = null
          return
        }

        this.activeColumn = column
      },
    },
  },

  methods: {

    /**
     * Formats row data by putting string values at the front.
     * @param {Array} items - array of table row values.
     * @returns {Array}
     */
    formatedRowData(items) {
      const values = items
      const nonNumericalValue = values.find(item => isNaN(item))
      const index = values.indexOf(nonNumericalValue)

      if (index > -1) {
        values.splice(index, 1)
        values.unshift(nonNumericalValue)

        return values
      }

      return values
    },

    /**
     * Formats cell data for display in the table.
     * @param {String} size - The string representing that size data.
     * @param {Number} index - The index within the loop.
     * @returns {String}
     */
    formatTableCells(size, index) {
      const hyphenated = size.includes('-')
      const parts = hyphenated ? size.split('-') : [size]

      const formatted = parts.map((part) => {
        /* eslint-disable */
        part = part.trim()

        if (index) {
          part = part.replace(/[^0-9\.]/g, '')
        }

        if (isNaN(part)) {
          return part
        }

        part = Number(part)
        part = this.showInches && index ? Math.round((part / 2.54) * 10) / 10 : part
        return part
        /* eslint-enable */
      })

      return formatted.join(' - ')
    },

    /**
     * Formats field data for display in the table.
     * @param {String} field - The string representing that field data.
     * @returns {String}
     */
    formatHeaderField(field) {
      return field.replaceAll(' (cm)', '').replaceAll(' ', '<br>')
    },

    /**
     * Handles table click events.
     * @param {Number} index - Position in the loop of the clicked element.
     */
    handleCellClick(index) {
      if (!this.emitsClickEvents || index === this.variantColumn) {
        return
      }

      this.activeColumn = index
      this.$emit('column-updated', index)
    },
  },
}
</script>

<style lang="scss">
@import './size-guide-table';
</style>
